import React from 'react'
import styled from 'styled-components'

import { IndexedProduct, ProductLabelStatus } from '../../../../../../../graphql/search'
import useSiteMetadata from '../../../../../../../hooks/useSiteMetadata'
import useSanityImage from '../../../../../../../lib/sanity/hooks/useSanityImage'
import { isNotNull } from '../../../../../../../utils/collectionTools'
import LabelStatusIcon from '../../../../../../LabelStatusIcon'
import * as Styled from './styled'

import productImagePlaceholder from '../../../../../../../img/product-image-placeholder.png'

export type Result = Pick<
  IndexedProduct,
  'id' | 'urlKey' | 'labelStatus' | 'name' | 'sanityImageJson' | 'shortDescription'
>

export interface ProductListSearchResultProps {
  result: Result
  noNavigate?: boolean
  displayMode?: 'normal' | 'compact'
}

const StyledLabelStatusIcon = styled(LabelStatusIcon)`
  position: absolute;
  margin-left: 3px;
`

type ProductListSearchResultContentProps = Omit<ProductListSearchResultProps, 'noNavigate'>

const ProductListSearchResultContent: React.VFC<ProductListSearchResultContentProps> = ({
  result,
  displayMode = 'normal',
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const sanityImageUrl = useSanityImage(
    { sourceJson: result.sanityImageJson || undefined },
    {
      dpr: 2,
      fit: 'max',
      // set width only to avoid crop bug: https://github.com/sanity-io/image-url/issues/32
      width: 180,
    },
  )
  return (
    <>
      <Styled.ItemImageContainer $displayMode={displayMode}>
        {featureFlags?.productLabelStatus && isNotNull(result.labelStatus) && (
          <StyledLabelStatusIcon
            isLabelPresent={result.labelStatus === ProductLabelStatus.Present}
          />
        )}
        <Styled.ItemImage src={sanityImageUrl || productImagePlaceholder} alt={result.name} />
      </Styled.ItemImageContainer>
      <Styled.ItemTitle>
        {result.name}
        <Styled.ItemDescription $displayMode={displayMode}>
          {(result.shortDescription || {}).text || ''}
        </Styled.ItemDescription>
      </Styled.ItemTitle>
    </>
  )
}

export const ProductListSearchResult: React.VFC<ProductListSearchResultProps> = ({
  result,
  displayMode = 'normal',
  noNavigate,
}) => {
  if (noNavigate) {
    return (
      <Styled.ItemContainer>
        <ProductListSearchResultContent result={result} displayMode={displayMode} />
      </Styled.ItemContainer>
    )
  }
  return (
    <Styled.ItemContainerLink to={`/products/${result.urlKey}/`}>
      <ProductListSearchResultContent result={result} displayMode={displayMode} />
    </Styled.ItemContainerLink>
  )
}
